(() => {
  'use strict';

  const config = window.Jamairo || {};
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const drawerTriggers = new WeakMap();

  function announce(message) {
    const region = document.getElementById('StatusMessage');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 50);
  }

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(focusableSelector)).filter(
      (element) => !element.hasAttribute('hidden') && element.getClientRects().length > 0
    );
  }

  function trapFocus(event, container) {
    if (event.key !== 'Tab') return;
    const focusable = getFocusable(container);
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function resolveTarget(trigger, attribute) {
    const value = trigger.getAttribute(attribute);
    if (!value) return null;
    try {
      return document.querySelector(value.startsWith('#') ? value : `#${CSS.escape(value)}`);
    } catch (_error) {
      return null;
    }
  }

  function openDrawer(drawer, trigger) {
    if (!drawer) return;
    drawerTriggers.set(drawer, trigger || document.activeElement);
    if (drawer instanceof HTMLDialogElement) {
      if (!drawer.open) drawer.showModal();
    } else {
      drawer.hidden = false;
      drawer.setAttribute('open', '');
      drawer.classList.add('is-open');
    }
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    requestAnimationFrame(() => {
      const autofocus = drawer.querySelector('[autofocus]');
      const focusable = getFocusable(drawer);
      (autofocus || focusable[0] || drawer).focus({ preventScroll: true });
    });
  }

  function closeDrawer(drawer) {
    if (!drawer) return;
    if (drawer instanceof HTMLDialogElement) {
      if (drawer.open) drawer.close();
    } else {
      drawer.removeAttribute('open');
      drawer.classList.remove('is-open');
      window.setTimeout(() => {
        if (!drawer.hasAttribute('open')) drawer.hidden = true;
      }, prefersReducedMotion ? 0 : 330);
    }
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('is-locked');
    const trigger = drawerTriggers.get(drawer);
    if (trigger instanceof HTMLElement) trigger.focus({ preventScroll: true });
  }

  function bindDrawers(scope = document) {
    scope.querySelectorAll('[data-drawer-open]').forEach((trigger) => {
      if (trigger.dataset.drawerBound === 'true') return;
      trigger.dataset.drawerBound = 'true';
      trigger.addEventListener('click', (event) => {
        const drawer = resolveTarget(trigger, 'data-drawer-open');
        if (!drawer) return;
        event.preventDefault();
        openDrawer(drawer, trigger);
      });
    });

    scope.querySelectorAll('[data-drawer]').forEach((drawer) => {
      if (drawer.dataset.drawerBound === 'true') return;
      drawer.dataset.drawerBound = 'true';
      drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeDrawer(drawer);
          return;
        }
        trapFocus(event, drawer);
      });
      drawer.querySelectorAll('[data-drawer-close], .drawer__overlay').forEach((control) => {
        control.addEventListener('click', () => closeDrawer(drawer));
      });
      if (drawer instanceof HTMLDialogElement) {
        drawer.addEventListener('cancel', (event) => {
          event.preventDefault();
          closeDrawer(drawer);
        });
        drawer.addEventListener('click', (event) => {
          if (event.target === drawer) closeDrawer(drawer);
        });
      }
    });
  }

  function bindDetails(scope = document) {
    scope.querySelectorAll('details').forEach((details) => {
      if (details.dataset.detailsBound === 'true') return;
      details.dataset.detailsBound = 'true';
      details.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && details.open) {
          details.open = false;
          details.querySelector('summary')?.focus();
        }
      });
    });

    if (document.documentElement.dataset.detailsOutsideBound !== 'true') {
      document.documentElement.dataset.detailsOutsideBound = 'true';
      document.addEventListener('click', (event) => {
        document.querySelectorAll('details[data-close-outside][open]').forEach((details) => {
          if (!details.contains(event.target)) details.open = false;
        });
      });
    }
  }

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';
      this.input = this.querySelector('input[type="number"]');
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button || !this.input) return;
        event.preventDefault();
        const previous = this.input.value;
        if (button.name === 'plus') this.input.stepUp();
        if (button.name === 'minus') this.input.stepDown();
        if (this.input.value !== previous) {
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  if (!customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInput);
  }

  function formatMoney(cents, format = config.moneyFormat || '${{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents || 0);
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    const type = match ? match[1] : 'amount';
    const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (!Number.isFinite(number)) return '0';
      const parts = (number / 100).toFixed(precision).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    };
    let amount;
    switch (type) {
      case 'amount_no_decimals':
        amount = formatWithDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        amount = formatWithDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = formatWithDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = formatWithDelimiters(value, 2, "'", '.');
        break;
      default:
        amount = formatWithDelimiters(value);
    }
    return format.replace(placeholder, amount);
  }

  async function getCart() {
    const response = await fetch(`${config.routes?.cart || '/cart'}.js`, {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(config.strings?.cartError || 'Cart error');
    return response.json();
  }

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = count;
      element.hidden = count < 1;
    });
    document.querySelectorAll('[data-cart-link]').forEach((element) => {
      const template = element.dataset.cartLabel;
      if (template) element.setAttribute('aria-label', template.replace('[count]', count));
    });
  }

  function replaceSection(id, html) {
    if (!html) return;
    const currentWrapper = document.getElementById(`shopify-section-${id}`);
    if (currentWrapper) {
      currentWrapper.innerHTML = html;
      bindTheme(currentWrapper);
      return;
    }
    const parser = new DOMParser();
    const documentFragment = parser.parseFromString(html, 'text/html');
    const incoming = documentFragment.querySelector(`[data-section-id="${id}"], [data-${id}]`);
    const current = document.querySelector(`[data-section-id="${id}"], [data-${id}]`);
    if (incoming && current) {
      current.replaceWith(incoming);
      bindTheme(incoming);
    }
  }

  async function refreshCartDrawer(sectionHtml) {
    if (sectionHtml) {
      replaceSection('cart-drawer', sectionHtml);
    } else {
      const separator = (config.routes?.cart || '/cart').includes('?') ? '&' : '?';
      const response = await fetch(`${config.routes?.cart || '/cart'}${separator}section_id=cart-drawer`);
      if (!response.ok) throw new Error(config.strings?.cartError || 'Cart error');
      replaceSection('cart-drawer', await response.text());
    }
    const cart = await getCart();
    updateCartCount(cart.item_count);
    return cart;
  }

  async function addToCart(form, submitter) {
    const button = submitter || form.querySelector('[type="submit"]');
    if (button?.classList.contains('is-loading')) return;
    button?.classList.add('is-loading');
    button?.setAttribute('aria-disabled', 'true');

    const formData = new FormData(form);
    formData.set('sections', 'cart-drawer');
    formData.set('sections_url', window.location.pathname);

    try {
      const response = await fetch(`${config.routes?.cartAdd || '/cart/add'}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || config.strings?.cartError);
      }

      await refreshCartDrawer(result.sections?.['cart-drawer']);
      const drawer = document.querySelector('[data-cart-drawer]');
      const productTitle = result.product_title || result.title || '';
      announce(
        productTitle
          ? (form.dataset.successMessage || `${productTitle} added to cart`)
          : (form.dataset.successMessage || config.strings?.addToCart)
      );

      if (config.cartDrawerEnabled && drawer) {
        openDrawer(drawer, button);
      } else {
        window.location.assign(config.routes?.cart || '/cart');
      }
    } catch (error) {
      const message = error.message || config.strings?.cartError || 'Cart error';
      const errorElement = form.querySelector('[data-product-form-error]');
      if (errorElement) {
        errorElement.textContent = message;
        errorElement.hidden = false;
      }
      announce(message);
    } finally {
      button?.classList.remove('is-loading');
      button?.removeAttribute('aria-disabled');
    }
  }

  function bindProductForms(scope = document) {
    scope.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      if (form.dataset.ajaxBound === 'true') return;
      form.dataset.ajaxBound = 'true';
      form.addEventListener('submit', (event) => {
        if (event.defaultPrevented) return;
        const submitter = event.submitter;
        if (
          submitter?.name === 'checkout' ||
          submitter?.classList.contains('shopify-payment-button__button') ||
          submitter?.closest('.shopify-payment-button')
        ) {
          return;
        }
        event.preventDefault();
        addToCart(form, submitter);
      });
    });
  }

  function bindCartLinks(scope = document) {
    if (!config.cartDrawerEnabled) return;
    scope.querySelectorAll('[data-cart-open]').forEach((link) => {
      if (link.dataset.cartBound === 'true') return;
      link.dataset.cartBound = 'true';
      link.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const drawer = document.querySelector('[data-cart-drawer]');
        if (!drawer) return;
        event.preventDefault();
        openDrawer(drawer, link);
      });
    });
  }

  function bindWishlist(scope = document) {
    const storageKey = 'jamairo:wishlist';
    let saved = [];
    try {
      saved = JSON.parse(localStorage.getItem(storageKey) || '[]');
      if (!Array.isArray(saved)) saved = [];
    } catch (_error) {
      saved = [];
    }

    scope.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      if (button.dataset.wishlistBound === 'true') return;
      button.dataset.wishlistBound = 'true';
      const handle = button.dataset.productHandle;
      const update = () => {
        const active = saved.includes(handle);
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', active ? button.dataset.labelRemove : button.dataset.labelAdd);
      };
      update();
      button.addEventListener('click', () => {
        if (!handle) return;
        saved = saved.includes(handle) ? saved.filter((item) => item !== handle) : [...saved, handle];
        try {
          localStorage.setItem(storageKey, JSON.stringify(saved.slice(-100)));
        } catch (_error) {
          // Browsers can disable storage; the control remains safe and session-only.
        }
        update();
        announce(button.getAttribute('aria-label'));
      });
    });
  }

  function bindVideoControls(scope = document) {
    scope.querySelectorAll('[data-video-play]').forEach((button) => {
      if (button.dataset.videoBound === 'true') return;
      button.dataset.videoBound = 'true';
      button.addEventListener('click', () => {
        const container = button.closest('[data-video-container]') || button.parentElement;
        const video = container?.querySelector('video');
        if (!video) return;
        if (video.paused) {
          video.play();
          container.classList.add('is-playing');
        } else {
          video.pause();
          container.classList.remove('is-playing');
        }
      });
    });
  }

  function bindReveal(scope = document) {
    const elements = scope.querySelectorAll('.reveal:not([data-reveal-bound])');
    if (!elements.length) return;
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries, instance) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          instance.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    elements.forEach((element) => {
      element.dataset.revealBound = 'true';
      observer.observe(element);
    });
  }

  function bindHeader() {
    const header = document.querySelector('[data-site-header]');
    if (!header || header.dataset.scrollBound === 'true') return;
    header.dataset.scrollBound = 'true';
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 24);
      ticking = false;
    };
    update();
    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      },
      { passive: true }
    );
  }

  function bindTheme(scope = document) {
    bindDrawers(scope);
    bindDetails(scope);
    bindProductForms(scope);
    bindCartLinks(scope);
    bindWishlist(scope);
    bindVideoControls(scope);
    bindReveal(scope);
    bindHeader();
  }

  window.JamairoTheme = {
    announce,
    openDrawer,
    closeDrawer,
    getCart,
    refreshCartDrawer,
    replaceSection,
    updateCartCount,
    formatMoney,
    bind: bindTheme
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bindTheme());
  } else {
    bindTheme();
  }

  document.addEventListener('shopify:section:load', (event) => bindTheme(event.target));
  document.addEventListener('shopify:section:select', (event) => {
    event.target.querySelector('.reveal')?.classList.add('is-visible');
  });
})();
